import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';

import { AuthProvider, useAuth } from '@/lib/auth';
import { CartProvider } from '@/lib/cart';
import AppShell from '@/components/Layout/AppShell';

import Home from '@/pages/Home';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import AuthCallback from '@/pages/AuthCallback';
import Assessment from '@/pages/Assessment';
import AssessmentResult from '@/pages/AssessmentResult';
import DietConsultation from '@/pages/DietConsultation';
import Consultation from '@/pages/Consultation';
import Products from '@/pages/Products';
import ProductDetail from '@/pages/ProductDetail';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import Dashboard from '@/pages/Dashboard';
import Learn from '@/pages/Learn';
import LearnArticle from '@/pages/LearnArticle';
import About from '@/pages/About';
import Founder from '@/pages/Founder';
import { ClinicalEvidenceList, ClinicalEvidenceDetail } from '@/pages/ClinicalEvidence';

// Admin
import AdminShell from '@/components/admin/AdminShell';
import AdminHome from '@/pages/admin/AdminHome';
import AdminProducts from '@/pages/admin/AdminProducts';
import AdminOrders from '@/pages/admin/AdminOrders';
import AdminAppointments from '@/pages/admin/AdminAppointments';
import AdminAssessments from '@/pages/admin/AdminAssessments';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminDoctors from '@/pages/admin/AdminDoctors';
import AdminArticles from '@/pages/admin/AdminArticles';
import AdminTestimonials from '@/pages/admin/AdminTestimonials';
import AdminFAQs from '@/pages/admin/AdminFAQs';
import AdminConcerns from '@/pages/admin/AdminConcerns';
import AdminEvidence from '@/pages/admin/AdminEvidence';
import AdminCoupons from '@/pages/admin/AdminCoupons';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminVerses from '@/pages/admin/AdminVerses';
import AdminAnalytics from '@/pages/admin/AdminAnalytics';
import AdminAIWriter from '@/pages/admin/AdminAIWriter';
import AdminDietPlans from '@/pages/admin/AdminDietPlans';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';

// Doctor
import DoctorShell from '@/components/doctor/DoctorShell';
import DoctorHome from '@/pages/doctor/DoctorHome';
import DoctorAppointment from '@/pages/doctor/DoctorAppointment';
import DoctorPatient from '@/pages/doctor/DoctorPatient';
import DoctorDietPlans from '@/pages/doctor/DoctorDietPlans';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-sans_mute text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRouter() {
  const loc = useLocation();
  if (loc.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Admin (separate shell, no public header) */}
        <Route path="/admin" element={<Protected roles={['admin']}><AdminShell /></Protected>}>
          <Route index element={<AdminHome />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="appointments" element={<AdminAppointments />} />
          <Route path="assessments" element={<AdminAssessments />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="doctors" element={<AdminDoctors />} />
          <Route path="articles" element={<AdminArticles />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="faqs" element={<AdminFAQs />} />
          <Route path="concerns" element={<AdminConcerns />} />
          <Route path="evidence" element={<AdminEvidence />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="verses" element={<AdminVerses />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="ai-writer" element={<AdminAIWriter />} />
          <Route path="diet-plans" element={<AdminDietPlans />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        {/* Doctor */}
        <Route path="/doctor" element={<Protected roles={['doctor', 'admin']}><DoctorShell /></Protected>}>
          <Route index element={<DoctorHome />} />
          <Route path="diet-plans" element={<DoctorDietPlans />} />
          <Route path="appointments/:apptId" element={<DoctorAppointment />} />
          <Route path="patients/:userId" element={<DoctorPatient />} />
        </Route>

        {/* Public shell */}
        <Route element={<AppShell />}>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/assessment" element={<Protected><Assessment /></Protected>} />
          <Route path="/assessment/:id" element={<Protected><AssessmentResult /></Protected>} />
          <Route path="/consultation" element={<Consultation />} />
          <Route path="/consultation/diet" element={<DietConsultation />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:slug" element={<ProductDetail />} />
          <Route path="/cart" element={<Protected><Cart /></Protected>} />
          <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/learn" element={<Learn />} />
          <Route path="/learn/:slug" element={<LearnArticle />} />
          <Route path="/clinical-evidence" element={<ClinicalEvidenceList />} />
          <Route path="/clinical-evidence/:slug" element={<ClinicalEvidenceDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/founder" element={<Founder />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-center" richColors={false} closeButton />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
